import { useLazyQuery } from '@apollo/client';
import { PrimaryButton, Stack, TextField } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { cutToast } from 'utility/toast';
import {
  TransferCycle,
  TransferCycleVariables,
} from 'common/graphql/__generated__/TransferCycle';
import React, { useState } from 'react';
import { appendExtension, extractData } from 'common/utils';
import toast from 'react-hot-toast';
import { exportIcon, useStyles } from './index.styles';
import { GraphQLFormattedError } from 'graphql';
import { CsvDownloadAsLink } from './CsvDownloadAsLink';

import TRANSFER_CYCLE from 'common/graphql/TransferCycle.graphql';

export interface ExportToCsvProps {
  isButtonVisible: boolean;
  title: string | (() => string);
  getExportCycle: (description: string | undefined) => Promise<{
    id?: string | null;
    errors?: readonly GraphQLFormattedError[];
  }>;
  onDialogOpen?: () => void;
  onDialogClose?: () => void;
  additionalComponents?: React.ReactNode;
  onConfirmOverride?: (actions: {
    confirm: () => void;
    close: () => void;
  }) => void;
}

interface CsvData {
  contents: string[][];
  filename: string;
}

const EMPTY_RESULTS_TEXT = 'Nothing to export';

export const ExportToCsv: React.FC<ExportToCsvProps> = ({
  isButtonVisible,
  title,
  getExportCycle,
  onDialogClose,
  onDialogOpen,
  additionalComponents,
  onConfirmOverride,
}) => {
  const styles = useStyles();

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [description, setDescription] = useState<string>();

  const [csv, setCsv] = useState<CsvData>();

  const showDialog = () => {
    onDialogOpen?.();
    setDescription(undefined);
    setCsv(undefined);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    onDialogClose?.();
    setDialogVisible(false);
  };

  const [getTransferCycle] = useLazyQuery<
    TransferCycle,
    TransferCycleVariables
  >(TRANSFER_CYCLE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const handleExport = async () => {
    setIsExporting(true);
    const toastId = cutToast.info('Exporting');

    // NOTE: Export cycles and transfer cycles should now always return a single element. We
    //       previously had these arrays populated with >1 values to support an earlier
    //       requirement that resulted in multiple (or combined) export files.
    try {
      const { id: exportCycleId, errors: exportFilterErrors } =
        await getExportCycle(description);

      if (exportFilterErrors?.length) {
        finishExport(toastId, exportFilterErrors?.[0].message);
        return;
      }
      if (!exportCycleId) {
        finishExport(toastId, EMPTY_RESULTS_TEXT);
        return;
      }

      const { errors: transferErrors, data: transferData } =
        await getTransferCycle({
          variables: { id: exportCycleId },
        });

      if (transferErrors?.length) {
        finishExport(toastId, transferErrors?.[0].message);
        return;
      }
      if (!transferData?.transferCycle) {
        finishExport(toastId, EMPTY_RESULTS_TEXT);
        return;
      }

      const headers = extractData(transferData.transferCycle.transferHeaders);
      const data = transferData.transferCycle.transactionTransfers.nodes.map(
        (row) => extractData(row.transferValues)
      );
      setCsv({
        filename:
          appendExtension(transferData.transferCycle._exportFilename, 'csv') ??
          `export_${new Date().toISOString()}.csv`,
        contents: [headers, ...data],
      });

      finishExport(toastId);
    } catch (err: any) {
      finishExport(err.message ?? 'Export failed');
    }
  };

  const finishExport = (toastId: string, error?: string) => {
    setIsExporting(false);

    if (error) cutToast.error(error, { id: toastId });
    else cutToast.success('Export complete', { id: toastId });
  };

  const onConfirm = () => {
    handleExport();
    hideDialog();
  };

  return (
    <>
      {isButtonVisible && (
        <PrimaryButton
          text="Export"
          onClick={showDialog}
          iconProps={exportIcon}
          disabled={isExporting}
        />
      )}
      {csv && (
        <CsvDownloadAsLink
          data={csv.contents}
          filename={csv.filename}
          target="_blank"
        />
      )}

      <ConfirmDialog
        isConfirmPrimaryButton
        hidden={!isDialogVisible}
        title={typeof title === 'string' ? title : title()}
        onDismiss={hideDialog}
        minWidth={500}
        onConfirm={() => {
          if (onConfirmOverride) {
            onConfirmOverride({
              confirm: onConfirm,
              close: hideDialog,
            });
          } else {
            onConfirm();
          }
        }}
      >
        <Stack tokens={{ childrenGap: 20 }}>
          <TextField
            label="Description"
            className={styles.description}
            rows={3}
            value={description}
            onChange={(_event, value) => setDescription(value || '')}
            placeholder="Worksheet description"
            resizable={true}
          />
          {additionalComponents}
        </Stack>
      </ConfirmDialog>
    </>
  );
};
